import { IconButton as MuiIconButton, SxProps, Theme } from '@mui/material';
import { forwardRef } from 'react';

/**
 * Size variants with fixed pixel dimensions.
 * - 'lg': 48x48px (primary action)
 * - 'md': 40x40px (secondary action)
 * - 'sm': 25x25px (close-only use)
 */
export type CircularIconButtonSize = 'lg' | 'md' | 'sm';

export type CircularIconButtonColor =
  | 'primary'
  | 'secondary'
  | 'info'
  | 'error'
  | 'success'
  | 'warning';

export interface CircularIconButtonProps {
  /**
   * The size of the circular button.
   * - 'lg': 48x48px (primary action)
   * - 'md': 40x40px (secondary action)
   * - 'sm': 25x25px (close-only use)
   * @default 'md'
   */
  size?: CircularIconButtonSize;

  /**
   * The color of the component. Maps to theme palette colors.
   * @default 'primary'
   */
  color?: CircularIconButtonColor;

  /**
   * If true, the button renders in its active state with a filled background.
   * If false, renders as an outlined gray circle with gray icon.
   * Use for toggling states like "Filters On/Off", etc.
   * @default true
   */
  isActive?: boolean;

  /**
   * If true, the button will be disabled.
   * @default false
   */
  disabled?: boolean;

  /**
   * The content of the button (typically an icon).
   */
  children: React.ReactNode;

  /**
   * Custom styles applied to the root element.
   */
  sx?: SxProps<Theme>;

  /**
   * Class name applied to the root element.
   */
  className?: string;

  /**
   * Callback fired when the button is clicked.
   */
  onClick?: React.MouseEventHandler<HTMLButtonElement>;

  /**
   * The aria-label attribute for accessibility. Required for icon-only buttons.
   */
  'aria-label': string;

  /**
   * The data-testid attribute for testing.
   */
  'data-testid'?: string;
}

const CIRCULAR_ICON_BUTTON_SIZES: Record<CircularIconButtonSize, { button: number; icon: number }> =
  {
    lg: { button: 48, icon: 30 },
    md: { button: 40, icon: 24 },
    sm: { button: 24, icon: 16 },
  };

/**
 * CircularIconButton Component
 *
 * A circular icon button with three size variants and optional active state.
 * Uses MUI IconButton internally with fixed dimensions and filled background.
 *
 * @example
 * ```tsx
 * <CircularIconButton aria-label="edit" color="primary" size="lg">
 *   <EditIcon />
 * </CircularIconButton>
 * ```
 *
 * @example
 * ```tsx
 * // With active state for "filters applied"
 * <CircularIconButton aria-label="filters" color="info" isActive={hasActiveFilters}>
 *   <FilterIcon />
 * </CircularIconButton>
 * ```
 */
export const CircularIconButton = forwardRef<HTMLButtonElement, CircularIconButtonProps>(
  (
    {
      size = 'md',
      color = 'primary',
      isActive = true,
      disabled = false,
      children,
      sx,
      onClick,
      'aria-label': ariaLabel,
      'data-testid': dataTestId,
      ...props
    },
    ref
  ) => {
    const sizeConfig = CIRCULAR_ICON_BUTTON_SIZES[size];

    const baseStyles: SxProps<Theme> = (theme) => ({
      width: sizeConfig.button,
      height: sizeConfig.button,
      minWidth: sizeConfig.button,
      padding: 0,
      borderRadius: '50%',
      ...(isActive ? { boxShadow: theme.shadows[2] } : { boxShadow: 'none' }),
      '& .MuiSvgIcon-root': {
        fontSize: sizeConfig.icon,
      },
      '&:hover': {
        backgroundColor: theme.palette[color].dark,
      },
      ...(isActive && {
        backgroundColor: theme.palette[color].main,
        color: theme.palette.common.white,
        '&:hover': {
          backgroundColor: theme.palette[color].dark,
        },
      }),
      // Inactive state: outlined gray circle with gray icon
      ...(!isActive && {
        backgroundColor: 'transparent',
        border: `3px solid ${theme.palette.action.active}`,
        color: theme.palette.action.active,
        '&:hover': {
          backgroundColor: theme.palette.action.hover,
        },
      }),
      '&.Mui-disabled': {
        backgroundColor: theme.palette.action.disabledBackground,
        color: theme.palette.action.disabled,
        border: 'none',
        pointerEvents: 'none',
      },
    });

    return (
      <MuiIconButton
        ref={ref}
        disabled={disabled}
        onClick={onClick}
        aria-label={ariaLabel}
        data-testid={dataTestId}
        sx={[baseStyles, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
        {...props}
      >
        {children}
      </MuiIconButton>
    );
  }
);

CircularIconButton.displayName = 'CircularIconButton';
